import React, { useState, useContext } from 'react';
import { Link, useNavigate, useLoaderData } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BasketContext } from './context-basket';
import * as Basket from './page-basket';
import * as Product from './product';
import * as Algolia from './algolia';
import { Footer, pillButtonClassName } from './template';
import { ScanButton } from './scanner/scan-button';
import { getTaxonomy, slugPathToURL, CATEGORY_ATTRIBUTES } from './category-routes';
import iconBasket from 'url:./assets/icon-basket.svg';
import iconSearch from 'url:./assets/icon-search.svg';

// Top-level categories that actually have products right now. The taxonomy
// gives titles + slugs in their canonical order, but it also contains
// near-empty top levels (marginal marketing trees, e.g. "Vegan" with 3
// products) that would send visitors to effectively empty category pages
// -- so it's intersected with a live facet-count query, the same liveness
// idea the sitemap uses, and a floor high enough to keep stragglers out.
// Both inputs are nice-to-haves: any failure just renders the landing
// page without category links.
const MIN_CATEGORY_PRODUCTS = 50;
async function liveTopCategories() {
  const [taxonomy, facetsRes] = await Promise.all([
    getTaxonomy(),
    Algolia.products.search('', {
      hitsPerPage: 0,
      facets: [CATEGORY_ATTRIBUTES[0]],
    }),
  ]);
  const counts = facetsRes.facets?.[CATEGORY_ATTRIBUTES[0]] ?? {};
  return taxonomy.filter(
    (node) => (counts[node.s] ?? 0) >= MIN_CATEGORY_PRODUCTS
  );
}

export async function loader({ params }) {
  const [mostReduced, topCategories] = await Promise.all([
    Algolia.misc.getObject('most-reduced'),
    liveTopCategories().catch(() => []),
  ]);
  return { mostReduced, topCategories };
}

function BasketButton({ basket }) {
  const itemCount = basket.products
    ? Basket.sumItemCount(Object.values(basket.products))
    : 0;

  return (
    <Link to="/basket">
      <div className="bg-ui hover:bg-green-500 rounded-xl text-white w-[5.5rem]">
        <div className="flex items-center justify-center">
          <img src={iconBasket} className="w-10 h-10 p-1 mr-1" />
          <div className="w-7">{itemCount}</div>
        </div>
      </div>
    </Link>
  );
}

function LandingSearchBox() {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(
        `/search?${encodeURIComponent(Algolia.BASE_INDEX)}%5Bquery%5D=${encodeURIComponent(query.trim())}`
      );
    } else {
      navigate('/search');
    }
  };

  return (
    <form onSubmit={handleSearch} className="w-full max-w-2xl">
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Αναζήτηση προϊόντων..."
          className="w-full px-6 py-4 text-lg rounded-full border-2 border-gray-300 focus:border-lime-600 focus:outline-none pl-10"
        />
        <button
          type="submit"
          className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-lime-600 text-white px-6 py-2 rounded-full hover:bg-lime-700 transition-colors"
        >
          <span className="hidden sm:inline">Αναζήτηση</span>
          <img src={iconSearch} className="w-5 h-5 sm:hidden" alt="Αναζήτηση" />
        </button>
      </div>
    </form>
  );
}

const landingBreadcrumb = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Αρχική',
      item: 'https://www.zuper-market.gr/',
    },
  ],
};

function CategoryLinks({ categories }) {
  if (!categories || categories.length === 0) return null;

  return (
    <div className="mb-8">
      <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">
        Κατηγορίες
      </h2>
      <div className="flex flex-wrap justify-center gap-2 max-w-3xl mx-auto">
        {categories.map((node) => (
          <Link
            key={node.s}
            to={slugPathToURL([node.s])}
            className={pillButtonClassName({ variant: 'grey' })}
          >
            {node.t}
          </Link>
        ))}
      </div>
    </div>
  );
}

export function LandingPage() {
  const basket = useContext(BasketContext);
  const { mostReduced, topCategories } = useLoaderData();

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>
          zuper — Σύγκριση τιμών σούπερ μάρκετ από ΑΒ, Σκλαβενίτη, Lidl και άλλα
        </title>
        <meta
          name="description"
          content="Πόσο κάνει ένα προϊόν στο σούπερ μάρκετ; Σύγκρινε εύκολα τιμές από 13 αλυσίδες (ΑΒ Βασιλόπουλος, Σκλαβενίτης, Lidl, Μασούτης, My Market, Κρητικός κ.ά.), δες πόσο κάνει το καθένα, τις μεγαλύτερες πτώσεις τιμών και υπολόγισε το καλάθι σου."
        />
        <meta
          name="keywords"
          content="πόσο κάνει, ποσο κανει, poso kanei, posokanei, σύγκριση τιμών σούπερ μάρκετ, τιμές σούπερ μάρκετ, σύγκριση τιμών"
        />
        <link rel="canonical" href="https://www.zuper-market.gr/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.zuper-market.gr/" />
        <meta
          property="og:title"
          content="zuper — Σύγκριση τιμών σούπερ μάρκετ"
        />
        <meta
          property="og:description"
          content="Σύγκρινε τιμές σε 13 αλυσίδες σούπερ μάρκετ και δες τις μεγαλύτερες πτώσεις τιμών."
        />
        <script type="application/ld+json">
          {JSON.stringify(landingBreadcrumb)}
        </script>
      </Helmet>

      {/* Header with basket */}
      <div className="flex justify-between items-center p-4 md:grid md:grid-cols-3">
        {/* Plain <a> (full document load) on purpose: /intelligence is excluded
            from AdSense Auto Ads at the URL level, but Auto Ads only re-reads
            that exclusion on a real page load — an SPA <Link> would leak ads
            onto the page. */}
        <a
          href="/intelligence"
          className="inline-flex items-center gap-2 bg-lime-50 hover:bg-lime-100 border border-lime-200 text-lime-700 font-semibold text-sm md:text-base px-4 py-1.5 rounded-full transition-colors md:col-start-2 md:justify-self-center"
        >
          Θέλετε τιμές B2B;
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2.5}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
            />
          </svg>
        </a>
        <div className="md:col-start-3 md:justify-self-end">
          <BasketButton basket={basket} />
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Big logo */}
        <div className="text-center mb-12">
          <h1 className="text-8xl md:text-9xl text-lime-600 font-extrabold tracking-wide mb-8">
            zuper
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Πόσο κάνει ένα προϊόν στο σούπερ μάρκετ; Αναζητήστε προϊόντα από
            πολλαπλά super market και συγκρίνετε εύκολα τιμές για να δείτε πόσο
            κάνει το καθένα!
          </p>
        </div>

        {/* Search bar */}
        <div className="flex justify-center mb-4">
          <LandingSearchBox />
        </div>

        {/* Barcode scan entry point -- mobile only; desktop users have a
            keyboard, so the point-your-phone-at-a-barcode flow doesn't
            apply there. */}
        <div className="lg:hidden flex justify-center mb-8">
          <div className="w-full max-w-2xl sm:flex sm:justify-center">
            <ScanButton variant="prominent" />
          </div>
        </div>

        {/* Top-level category links */}
        <CategoryLinks categories={topCategories} />

        {/* Latest blog post. Plain <a> (full document load): the blog is
            static HTML under /blog/, not an SPA route. */}
        <div className="flex justify-center mb-8">
          <a
            href="/blog/plithorismos-super-market-2024-2026.html"
            className="group inline-flex items-center gap-3 bg-lime-50 hover:bg-lime-100 border border-lime-200 rounded-2xl px-5 py-3 max-w-2xl transition-colors"
          >
            <span className="shrink-0 bg-lime-600 text-white text-xs font-bold uppercase tracking-wide rounded-full px-2.5 py-1">
              Blog
            </span>
            <span className="text-gray-700 text-sm md:text-base">
              Δύο χρόνια, 88 εκατομμύρια τιμές: τι έγινε πραγματικά στα ράφια
              των σούπερ μάρκετ
            </span>
            <svg
              className="w-4 h-4 shrink-0 text-lime-700 group-hover:translate-x-0.5 transition-transform"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
              />
            </svg>
          </a>
        </div>

        {/* FAQ link */}
        <div className="text-center text-gray-500 mb-8">
          <p>
            Έχετε απορίες;{' '}
            <Link
              to="/faq"
              className="text-lime-600 hover:text-lime-700 underline"
            >
              Πατήστε εδώ
            </Link>
          </p>
        </div>

        {/* Most reduced products */}
        {mostReduced && mostReduced['most-reduced']?.rel && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">
              Μεγάλες πτώσεις τιμών
            </h2>

            <div className="mb-12">
              {/* Desktop view */}
              <div className="hidden sm:block">
                <div className="grid lg:grid-cols-5 md:grid-cols-4 sm:grid-cols-4 grid-cols-1">
                  {mostReduced['most-reduced'].rel.slice(0, 10).map((item) => (
                    <div className="m-1" key={item.id}>
                      <Product.DesktopHit hit={item} useHighlight={false} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Mobile view */}
              <div className="sm:hidden block">
                <div className="grid grid-cols-1 divide-y">
                  {mostReduced['most-reduced'].rel.slice(0, 10).map((item) => (
                    <div key={item.id}>
                      <Product.MobileHit hit={item} useHighlight={false} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="text-center">
              <Link
                to="/most-reduced"
                className="inline-block bg-lime-600 text-white px-8 py-3 rounded-full hover:bg-lime-700 transition-colors text-lg font-medium"
              >
                Δες όλες τις πτώσεις τιμών
              </Link>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
